import ClassNames from 'classnames'
import * as moment from 'moment'
import * as React from 'react'
import ClickOutside from 'react-click-outside'
import { DayPickerSingleDateController } from 'react-dates'
import 'react-dates/initialize'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import * as constants from '../../../static/constants'
import DateRangePickerCustomStyle from '../../../static/dateRangePickerCustomStyle'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  name: string
  field: string
  setIsFilterPanelOpen?(isOpen: boolean): void
  handleOnSubmit(filterValue: any): void
}

const initialParams = new URLSearchParams(window.location.search)
const getInitialValue = () => (initialParams.has('date') ? moment(initialParams.get('date')) : null)
const getInitialLabelName = initialValue =>
  moment.isMoment(initialValue) ? initialValue.format('M/D') : I18n.t('generic.date')

const useDateRange = ({ handleOnSubmit, setPanelIsOpen }) => {
  const initialValue = React.useMemo(() => getInitialValue(), [])
  const [labelName, setLabelName] = React.useState(getInitialLabelName(initialValue))
  const [selectedDate, setSelectedDate] = React.useState<moment.Moment>(initialValue)

  const hasValue = React.useCallback(() => moment.isMoment(selectedDate), [selectedDate])

  const onDateChange = React.useCallback(date => setSelectedDate(date), [])

  const onClear = React.useCallback(() => setSelectedDate(null), [])

  const onApply = React.useCallback(() => {
    const hasDate = moment.isMoment(selectedDate)

    const filterValue: any = {
      date: hasDate ? selectedDate.format('YYYY-MM-DD') : null,
    }

    if (hasDate) {
      setLabelName(`${selectedDate.format('M/D')}`)
    } else {
      setLabelName(I18n.t('generic.date'))
    }

    handleOnSubmit(filterValue)
    setPanelIsOpen(false)
  }, [selectedDate, handleOnSubmit, setPanelIsOpen])

  return {
    hasValue,
    labelName,
    onApply,
    onClear,
    onDateChange,
    selectedDate,
  }
}

const DateRangeFilter: React.FC<IProps> = props => {
  const [isOpen, setIsOpen] = React.useState(false)
  const isMobile = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_SMALL}px)`).matches
  const setPanelIsOpen = React.useCallback(
    (open: boolean) => {
      if (open === isOpen) {
        return
      }
      if (typeof props.setIsFilterPanelOpen === 'function') {
        props.setIsFilterPanelOpen(open)
      }
      setIsOpen(open)
    },
    [isOpen]
  )
  const { handleOnSubmit } = props
  const { hasValue, labelName, onApply, onClear, onDateChange, selectedDate } = useDateRange({
    handleOnSubmit,
    setPanelIsOpen,
  })

  return (
    <ClickOutside onClickOutside={() => setPanelIsOpen(false)}>
      <DateRangeFilterWrapper className="DateRangeFilter">
        <FilterLabel
          className={ClassNames('Filter_Label', { filled: isOpen || hasValue() })}
          onClick={() => setPanelIsOpen(!isOpen)}
        >
          {labelName}
          <SvgIcon
            name="keyboard_arrow_down"
            className="icon expand"
            ariaHidden={true}
            role="presentation"
          />
        </FilterLabel>
        {isOpen && (
          <div className="Filter_Main">
            <div className="Filter_Head">
              <div onClick={() => setPanelIsOpen(false)} className="close">
                <SvgIcon name="close" className="icon" ariaHidden={true} role="presentation" />
              </div>
            </div>
            <DateRangePickerCustomStyle />
            <DayPickerSingleDateController
              noBorder={true}
              date={selectedDate}
              onDateChange={date => onDateChange(date)}
              enableOutsideDays={false}
              numberOfMonths={isMobile ? 1 : 2}
              hideKeyboardShortcutsPanel={true}
              isOutsideRange={day => day.isSameOrBefore(moment(), 'day')}
            />
            <FilterFooter>
              <div className={ClassNames('_clear', { disabled: !hasValue() })} onClick={onClear}>
                {I18n.t('generic.clear')}
              </div>
              <div className="_apply" onClick={onApply}>
                {I18n.t('generic.apply')}
              </div>
            </FilterFooter>
          </div>
        )}
      </DateRangeFilterWrapper>
    </ClickOutside>
  )
}

const FilterLabel = styled.div`
  border: solid 1px ${constants.COLORS.divider};
  display: flex;
  align-items: center;
  padding: 2px 12px;
  font-size: 14px;
  border-radius: 18px;
  cursor: pointer;

  &.filled {
    background-color: ${constants.COLORS.primary};
    border: solid 1px ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
  }

  &:hover:not(.filled) {
    border: solid 1px ${constants.COLORS.primary};
    color: ${constants.COLORS.primary};
  }
`

const FilterFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 16px 12px 0;

  ._clear,
  ._apply {
    font-weight: bold;
    cursor: pointer;
  }

  ._clear {
    color: ${constants.COLORS.subText};

    &.disabled {
      visibility: hidden;
    }
  }

  ._apply {
    color: ${constants.COLORS.primary};
  }
`

const DateRangeFilterWrapper = styled.div`
  position: relative;

  & + & {
    margin-left: 12px;
  }

  .Filter_Main {
    position: absolute;
    left: 0;
    bottom: -8px;
    transform: translateY(100%);
    background-color: ${constants.COLORS.white};
    box-shadow: 0 1px 3px 0 rgba(21, 27, 38, 0.15);
    padding: 12px 16px;
    border-radius: 4px;
    z-index: 1100;
    .Filter_Head {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      .close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        transition: background-color 0.2s;
        cursor: pointer;
        &:hover {
          background-color: ${constants.COLORS.cardBG};
        }
      }
      .icon {
        width: 24px;
        height: 24px;
      }
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      position: fixed;
      top: 52px;
      left: 0;
      right: 0;
      height: calc(100vh - 52px);
      transform: none;
      width: auto;
      max-width: 100vw;
    }
  }

  .DayPicker {
    margin: auto;
  }
`

export default DateRangeFilter
